"use client"

import { useEffect, useRef } from "react"
import { useQuery, useQueryClient } from "@tanstack/react-query"

import { usePermissions } from "@/hooks/use-permissions"
import { useBillingOverviewQuery } from "@/hooks/queries/billing-overview"
import { getPlanFeatureAccessAction } from "@/server-actions/plan-feature-access"
import type { PlanFeatureAccess } from "@/lib/billing/feature-access/rules"

export const planFeatureAccessQueryPrefix = ["billing", "feature-access"] as const

/** Until it loads, or when it fails, nothing is locked: the server guards still refuse. */
export function usePlanFeatureAccessQuery() {
  const { activeTenantId } = usePermissions()

  return useQuery<PlanFeatureAccess>({
    queryKey: [...planFeatureAccessQueryPrefix, activeTenantId],
    enabled: Boolean(activeTenantId),
    staleTime: 60_000,
    queryFn: async () => {
      const result = await getPlanFeatureAccessAction({})
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
  })
}

/**
 * Refetches the locks when the billing snapshot shows another plan or subscription status —
 * after a checkout, a Shopify approval or an in-place change alike. Mount once, in the app shell.
 */
export function useRefetchPlanFeatureAccessOnPlanChange() {
  const queryClient = useQueryClient()
  const { data } = useBillingOverviewQuery()
  const planState = data ? `${data.plan?.id ?? ""}:${data.subscription?.status ?? ""}` : null
  const seen = useRef(planState)

  useEffect(() => {
    if (planState === null) return
    if (seen.current !== null && seen.current !== planState) {
      void queryClient.invalidateQueries({ queryKey: planFeatureAccessQueryPrefix })
    }
    seen.current = planState
  }, [planState, queryClient])
}
